import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Calendar, ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { formatDay } from 'utils/formatDate';
import { moneyText } from 'utils/money';
import { activeLocale } from 'i18n';
import { interventionDay } from 'pages/AdminIssues/components/upcomingAssignment';
import { windowLabel } from 'constants/timeWindows';
import { calendarDay, dayAndTime, parisToday, weekdayDay, windowEnds } from 'utils/interventionDate';
import { requestVariant, type RequestVariant, useMyInterventionRequests } from 'hooks/useMyInterventionRequests';
import type { MyInterventionRequest } from 'api/myInterventionRequestsApi';
import RequestDetail, { acceptedSameDay, CARD, DASH, HINT, StatusPill } from './RequestDetail';
import { requestTypeVisual } from './requestVisuals';

/**
 * The technician's requests, as a segment of `/issues` (D23): `BottomBar` and `Header` are hidden
 * in the WebView and the L'Oréal shells carry four frozen native tabs, so a route of its own would
 * be unreachable — exactly what happened to `/profile`.
 *
 * `?request=<id>` opens the detail, so the notification's deep link lands on it and the browser's
 * back leaves the detail rather than the app (D24).
 */

type Chip = 'all' | 'pending' | 'accepted' | 'declined';

/**
 * Four chips and **never five**. The mock-up draws « Expirées »; the status was deleted (D5),
 * there is no expiry job, and the chip would always read zero.
 */
const CHIPS: Chip[] = ['all', 'pending', 'accepted', 'declined'];

/** A `switch` of literal keys — a key built by concatenation is invisible to the parity check. */
const chipLabel = (t: TFunction, chip: Chip): string => {
  switch (chip) {
    case 'pending':
      return t('New');
    case 'accepted':
      return t('Accepted');
    case 'declined':
      return t('Declined');
    default:
      return t('All requests');
  }
};

/** His visit once accepted; before, the deadline and its window; a pre-L6 row keeps the ticket day. */
const cardSchedule = (t: TFunction, request: MyInterventionRequest): string => {
  if (request.status === 'accepted' && request.chosenDate && request.chosenTime) {
    return t('Visit {{day}} · {{time}}', { day: formatDay(request.chosenDate), time: request.chosenTime });
  }
  if (request.deadlineDate) {
    const day = formatDay(request.deadlineDate);
    const window = windowLabel(t, request.timeWindow);
    return window ? t('Deadline {{day}} · {{window}}', { day, window }) : t('Deadline {{day}}', { day });
  }
  return dayAndTime(interventionDay(request.interventionDate), request.interventionTime) || DASH;
};

/** F2/F3 — an unanswered L7 request: its day window and tariff; `null` sends the card to `cardSchedule`. */
const windowMeta = (t: TFunction, request: MyInterventionRequest): string | null => {
  const earliest = calendarDay(request.earliestDate);
  if (request.status === 'accepted' || !earliest || !request.deadlineDate) return null;
  const amount = request.tariff ? moneyText(request.tariff, activeLocale()) : null;
  if (earliest === request.deadlineDate) {
    const day = weekdayDay(earliest);
    return amount ? t('{{day}} · {{amount}}', { day, amount }) : t('On {{day}}', { day });
  }
  const range = t('Between {{start}} and {{end}}', windowEnds(earliest, request.deadlineDate));
  return [range, windowLabel(t, request.timeWindow), amount].filter(Boolean).join(' · ');
};

const CARD_BORDER: Partial<Record<RequestVariant, string>> = {
  reminded: 'border-amber-500 shadow-[0_4px_14px_-10px_rgba(245,158,11,0.6)] dark:border-amber-500',
  pending: 'border-sky-500 shadow-[0_4px_14px_-10px_rgba(59,130,246,0.6)] dark:border-sky-500',
};

const MyInterventionRequests = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useMyInterventionRequests();
  const [chip, setChip] = useState<Chip>('all');
  /** Held from the accept's start: a lost race's refetch drops the row before he reads why (CA4). */
  const [held, setHeld] = useState<MyInterventionRequest | null>(null);

  const requests = data ?? [];

  // Counted on every render and not memoised: `requests` is a fresh array each time, so a memo
  // keyed on it would recompute anyway — and the list is a technician's own, a few dozen rows.
  const counts: Record<Chip, number> = {
    all: requests.length,
    pending: requests.filter((request) => request.status === 'pending').length,
    accepted: requests.filter((request) => request.status === 'accepted').length,
    declined: requests.filter((request) => request.status === 'declined').length,
  };

  const today = parisToday();
  // Unseen reminders first (E15); `sort` is stable, so the server's newest-first order stays below.
  const shown = (chip === 'all' ? requests : requests.filter((request) => request.status === chip))
    .map((request) => ({ request, variant: requestVariant(request, today) }))
    .sort((a, b) => Number(b.variant === 'reminded') - Number(a.variant === 'reminded'));

  // `Number(null)` is 0 and `Number('zz')` is NaN, so both read as "no request asked for".
  const requestId = Number(searchParams.get('request'));
  const found = (requestId && requests.find((request) => request.id === requestId)) || null;
  // Adjusted during render (React's "storing information from previous renders"), not in an effect.
  if (!requestId && held) setHeld(null);
  const selected: MyInterventionRequest | null = found ?? (held?.id === requestId ? held : null);

  const openRequest = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('request', String(id));
    // Pushed, so the browser's own back leaves the detail and lands on the list.
    setSearchParams(next);
  };

  /** `replace`: the in-app exit must not leave a detail entry for back to return to. */
  const closeRequest = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('request');
    setSearchParams(next, { replace: true });
  };

  const backToList = () => {
    setChip('all');
    closeRequest();
  };

  if (isLoading) {
    return (
      <div className="space-y-2 py-2">
        <Skeleton className="h-8 w-2/3 rounded-full" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }

  // A failed read claims nothing: never « aucune demande » on an error — that is the same lie as a
  // green badge on a request that never loaded (R-028).
  if (error) {
    return (
      <div className={cn(CARD, 'p-4')} role="alert">
        <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
          {t('Unable to load your requests')}
        </p>
        <p className={cn('mt-1', HINT)}>{t('Check your connection, then try again.')}</p>
        <Button variant="outline" className="mt-3 h-11 w-full text-sm" onClick={() => refetch()}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  // An unknown id, or one that is not his, falls back to the list: the API answers 404 and there
  // is deliberately no existence oracle. Any other vanished row (cancelled by ops) goes back too.
  if (selected) {
    return (
      <RequestDetail
        key={selected.id}
        request={selected}
        sameDayMissions={(day) => acceptedSameDay(requests, selected, day)}
        onBack={closeRequest}
        onDone={backToList}
        onHold={(hold) => setHeld(hold ? selected : null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      {/* Full-bleed over the page's `px-4`: one scrollable row, so the four chips never wrap. */}
      <div className="-mx-4">
        <div className="flex gap-2 overflow-x-auto px-4 pb-[14px] pt-[5px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CHIPS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={chip === value}
              onClick={() => setChip(value)}
              className={cn(
                'relative inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-[13px] text-[13px] font-medium',
                // The pill stays the mock-up's 34 px; the tap target is a 44 px overlay on top of it.
                "after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']",
                chip === value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-zinc-300 bg-white text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50'
              )}
            >
              {chipLabel(t, value)}
              <span
                className={cn(
                  'inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-[6px] px-[5px] text-[11px] font-semibold',
                  chip === value
                    ? 'bg-white/20 text-primary-foreground'
                    : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
                )}
              >
                {counts[value]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {requests.length === 0 ? (
        // Never solicited. A technician of the out-of-scope desk sees this segment and will never
        // receive anything in V1 — an empty tab with no sentence reads as a breakdown.
        <p className={cn(CARD, 'p-4', HINT)}>
          {t('New intervention requests will appear here.')}
        </p>
      ) : shown.length === 0 ? (
        // A different situation, and a different sentence: the list is not empty, the filter is.
        <div className={cn(CARD, 'p-4')}>
          <p className={HINT}>{t('No request in this filter.')}</p>
          <Button variant="outline" className="mt-3 h-11 text-sm" onClick={() => setChip('all')}>
            {t('Show all requests')}
          </Button>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {shown.map(({ request, variant }) => {
            const visual = requestTypeVisual(request.requestType);
            const meta = windowMeta(t, request);
            return (
              <li key={request.id}>
                <button
                  type="button"
                  onClick={() => openRequest(request.id)}
                  className={cn(
                    'flex w-full flex-col gap-2.5 rounded-[14px] border bg-white px-[14px] py-[13px] text-left',
                    'transition active:scale-[.985] active:bg-zinc-100 dark:bg-zinc-900 dark:active:bg-zinc-800',
                    // The mock-up's `isNew`: a request still waiting on him is the one thing on
                    // this list that needs an action — amber once reminded and not yet opened.
                    CARD_BORDER[variant] ??
                      'border-zinc-200 shadow-[0_1px_2px_rgba(16,24,40,0.03)] dark:border-zinc-800'
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={cn(
                        'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px]',
                        visual.tint.bg,
                        visual.tint.fg
                      )}
                    >
                      <visual.icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-px leading-[1.3]">
                      <p className="truncate text-[13.5px] font-semibold tracking-[-0.01em] text-zinc-900 dark:text-zinc-50">
                        {request.store || DASH}
                      </p>
                      <p className="truncate text-[12px] text-zinc-500 dark:text-zinc-400">
                        {request.requestType || DASH}
                      </p>
                    </div>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500"
                      aria-hidden="true"
                    />
                  </div>
                  {/* The desk's name on every row (D27): 77 of 79 technicians hold the `*` joker,
                      so a mixed list is normal — and without it two missions of the same store on
                      two desks are indistinguishable. */}
                  {/* Wraps rather than truncating: the day and the time are never cut (FLOW-026 §9). */}
                  <div className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1.5 border-t border-zinc-200 pt-[11px] dark:border-zinc-800">
                    <p className="flex min-w-0 items-center gap-1.5 text-[12px] text-zinc-400 dark:text-zinc-500">
                      <Calendar className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
                      {/* The desk name gives way first: the day · time is what he sorts by (CA14). */}
                      {request.serviceDeskName && (
                        <span className="min-w-0 truncate">{request.serviceDeskName} ·</span>
                      )}
                      {/* The longer L7 meta wraps instead: its date and amount are never cut either. */}
                      <span className={meta ? 'min-w-0' : 'shrink-0 whitespace-nowrap'}>
                        {meta ?? cardSchedule(t, request)}
                      </span>
                    </p>
                    <StatusPill status={variant} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MyInterventionRequests;
